import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { Encumbrance } from '@models/encumbrance';
import {
	Debt,
	DefaultUndertaking,
	Undertaking,
	UndertakingType,
} from '@models/matter';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { DebtsDispatch } from '@state/matter/debts';
import { EncumbrancesDispatch } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	UndertakingsDispatch,
	UndertakingsState,
} from '@state/matter/undertakings';
import { cloneDeep, merge } from 'lodash';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { textareaEditorStates } from '../../../parties/components/file-salutations/file-salutations.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@UntilDestroy()
@Component({
	selector: 'app-undertaking-dialog',
	templateUrl: './undertaking-dialog.component.html',
	animations: [openClose()],
	standalone: false,
})
export class UndertakingDialogComponent implements OnInit {
	@ViewSelectSnapshot(UndertakingsState.getUndertaking) undertakingFilterFn: (
		id: string,
	) => Undertaking;
	@Select(TagsSelector.getTagInjectedDocument)
	parsedDocumentFilterFn$: Observable<(doc: string) => string>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	public undertaking: Undertaking;
	public parsedText: string;
	public editing = false;
	public states = textareaEditorStates;
	public child: Debt | Encumbrance | object = {};
	public relatedInstrumentsTextareaStates: {
		[id: string]: { state: boolean; parsedText: string };
	} = {};

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { undertakingId: string },
		private dialogRef: MatDialogRef<UndertakingDialogComponent>,
		private undertakingsDispatch: UndertakingsDispatch,
		private debtDispatch: DebtsDispatch,
		private encumbranceDispatch: EncumbrancesDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.undertaking = merge(
			cloneDeep(DefaultUndertaking),
			cloneDeep(this.undertakingFilterFn(this.data.undertakingId)),
		);

		this.child = this.undertaking?.child ? this.undertaking?.child : {};

		if (this.undertaking) {
			Object.entries(this.undertaking.relatedInstruments || {}).forEach(
				([key, value]) => {
					this.getParsedDocument$(value.text)
						.pipe(distinctUntilChanged(), take(2))
						.subscribe((parsedText) => {
							this.relatedInstrumentsTextareaStates[key] = {
								state: false,
								parsedText,
							};
						});
				},
			);

			this.setParsedDocument(this.undertaking.text);
		}
	}

	getParsedDocument$(document: string) {
		return this.parsedDocumentFilterFn$.pipe(map((filter) => filter(document)));
	}

	setFulfilled(checked: boolean) {
		if (checked) {
			this.analytics.logEvent(
				AnalyticsEvent.ENCUMBRANCE_MODAL_SET_FULFILLED_CHECKBOX_CLICK,
			);
		}
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	saveModal(): void {
		this.undertakingsDispatch.CreateOrUpdate(this.undertaking);

		if (
			this.undertaking?.type === UndertakingType.ENCUMBRANCE &&
			this.undertaking?.join
		) {
			this.encumbranceDispatch.CreateOrMergeEncumbrance(
				this.undertaking?.join,
				{
					fulfilled: this.undertaking.fulfilled,
					registeredOnDate:
						(this.child as Encumbrance)?.registeredOnDate ?? null,
					dischargedInstrumentNumber:
						(this.child as Encumbrance)?.dischargedInstrumentNumber ?? null,
				},
			);
		}

		if (
			this.undertaking?.type === UndertakingType.DEBT &&
			this.undertaking?.join
		) {
			this.debtDispatch.UpdateDebt(this.undertaking?.join, {
				fulfilled: this.undertaking.fulfilled,
			});
		}

		this.dialogRef.close();
	}

	setParsedDocument(documentText: string) {
		this.getParsedDocument$(documentText)
			.pipe(take(1))
			.subscribe((result) => {
				this.parsedText = result;
			});
	}
}
